import { ResumeTemplate } from '../../../mocks/resumeTemplates';

interface CompareModalProps {
  templates: ResumeTemplate[];
  onClose: () => void;
  onUseTemplate: (template: ResumeTemplate) => void;
}

export default function CompareModal({ templates, onClose, onUseTemplate }: CompareModalProps) {
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full h-full bg-white overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center">
              <i className="ri-file-compare-line text-white text-xl"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Compare Templates</h2>
              <p className="text-xs text-gray-500">Side-by-side comparison of {templates.length} templates</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-2xl"></i>
          </button>
        </div>

        {/* Compare Grid */}
        <div className="flex-1 overflow-auto p-6">
          <div className={`grid ${templates.length === 2 ? 'grid-cols-2' : 'grid-cols-3'} gap-6 h-full`}>
            {templates.map((template) => (
              <div key={template.name} className="bg-white rounded-2xl border-2 border-gray-200 overflow-hidden flex flex-col">
                {/* Template Preview */}
                <div className="relative bg-gray-100 flex-shrink-0" style={{ aspectRatio: '3/4' }}>
                  <div className="absolute top-0 left-0 right-0 h-1.5 z-10" style={{ backgroundColor: template.color }} />
                  <img
                    src={template.image}
                    alt={template.name}
                    className="w-full h-full object-cover object-top"
                  />
                  
                  {/* Badges */}
                  <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
                    {template.badge === 'Popular' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-violet-500 to-cyan-500 text-white shadow-md">
                        🔥 Popular
                      </span>
                    )}
                    {template.badge === 'New' && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500 text-white shadow-md">
                        ✨ New
                      </span>
                    )}
                  </div>

                  {/* Pages badge */}
                  <div className="absolute top-3 right-3 z-10">
                    <span className="px-2 py-0.5 bg-black/60 text-white text-xs font-medium rounded-full backdrop-blur-sm">
                      {template.pages}p
                    </span>
                  </div>
                </div>

                {/* Template Details */}
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{template.name}</h3>
                  
                  {/* Category */}
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="text-xs font-semibold px-2.5 py-1 rounded-full"
                      style={{ backgroundColor: template.color + '18', color: template.color }}
                    >
                      {template.category}
                    </span>
                  </div>

                  {/* Accent Color */}
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-medium text-gray-500">Accent Color:</span>
                    <div className="flex items-center gap-1.5">
                      <div
                        className="w-5 h-5 rounded-full border-2 border-white shadow-md"
                        style={{ backgroundColor: template.color }}
                      />
                      <span className="text-xs font-mono text-gray-700">{template.color}</span>
                    </div>
                  </div>

                  {/* ATS Badge */}
                  <div className="flex items-center gap-2 mb-3">
                    <i className="ri-shield-check-line text-emerald-500 text-sm"></i>
                    <span className="text-xs font-semibold text-emerald-600">ATS-Optimized</span>
                  </div>

                  {/* Tags */}
                  <div className="mb-4">
                    <p className="text-xs font-medium text-gray-500 mb-2">Style Tags:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {template.tags.map((tag) => (
                        <span key={tag} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs font-medium">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Features */}
                  <div className="mb-4 flex-1">
                    <p className="text-xs font-medium text-gray-500 mb-2">Key Features:</p>
                    <ul className="space-y-1.5">
                      {template.features.slice(0, 4).map((feature, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                          <i className="ri-check-line text-emerald-500 text-sm flex-shrink-0 mt-0.5"></i>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => onUseTemplate(template)}
                    className="w-full px-4 py-3 rounded-xl text-sm font-bold text-white transition-all hover:shadow-lg whitespace-nowrap cursor-pointer"
                    style={{ backgroundColor: template.color }}
                  >
                    <i className="ri-edit-line mr-2"></i>Use This Template
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}